import {Injectable} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ApiService} from './../api/api.service';
import {ConfigService} from './../../config/config/config.service';
import {StorageService} from './../storage/storage.service';
import {CanSaveInMemory} from './../CanSaveInMemory';
import {map} from 'rxjs/internal/operators';
import {AutofillRequest} from './../../contracts/autofill-request';
import {Order} from './../../contracts/order';
import {ResponseValidator} from './../../utils/response-validator';
import {localConsole} from './../../utils/local-console';
import {ReplaySubject, Observable} from 'rxjs';
import {UtilityCookieService} from './../../utils/cookie/utilitycookie.service';
import {MdpDrug} from './../../contracts/mdp-drug';

@Injectable({
  providedIn: 'root'
})
export class AutofillEligibilityService extends CanSaveInMemory {

  time = this.date.transform(new Date(), 'yyyy-MM-dd hh:mm:ss');
  /**
   * Subscription for auto fill eligibility
   */
  public asyncSDEligibility: ReplaySubject<any> = new ReplaySubject(1);
  /**
   * Static auto fill eligibility
   */
  public sdEligibility: boolean = false;
  autofillEnabled = UtilityCookieService.assertCookieValue('mdp_autofill', 'on');
  /**
   * Flag to signal an eligibility call is pending
   */
  pendingDetermination: boolean;

  constructor(private apiService: ApiService,
              private _config: ConfigService,
              private storage: StorageService,
              private date: DatePipe) {
    super();
  }

  /**
   * Call getSDEligibility to check eligibility for drug
   * in given order
   *
   * Save response on failure or success for one hour
   */
  getAutofillEligibility(order: Order, user: any, forceCall?: boolean, rxList?: MdpDrug[]): Observable<any> {
    this.pendingDetermination = true;
    const drugList = this.getDrugList(order, rxList);
    const memberId = user.data.memberId;
    const stateOfResidence = order.state ? order.state : '';

    const requestObj: AutofillRequest = {
      'requestMetaData': {
        'appName': this._config.getMdpAppName(),
        'channelName': this._config.get('channelName'),
        'deviceType': this._config.getDeviceType(),
        'deviceToken': '7777',
        'lineOfBusiness': 'ICE',
        'apiKey': this._config.get('mdp'),
        'source': this._config.getSource('mdp'),
        'securityType': 'apiKey',
        'responseFormat': 'JSON',
        'type': 'node',
        'tokenID': user.tokenId || this.storage.sessionStorage.getItem('token')
      },
      'requestPayloadData': {
        'data': {
          'memberID': memberId,
          'memberType': 'rxConnectID',
          'encrypted': true
        },
        'additionalData': {
          'origin': 'MDP Dashboard',
          'isPatientRules': 'Y',
          'isRxRules': 'Y',
          'isAutofillRules': 'Y',
          'stateOfResidence': stateOfResidence,
          'currentTimeStamp': this.time,
          'drugDetails': drugList
        }
      }
    };
    return (!forceCall && this.pullFromMemory(requestObj.requestPayloadData)
      || this.apiService.post('getSDEligibility', null, requestObj))
      .pipe(
        map((response: any) => {
          this.pendingDetermination = false;
          this.pushToQ(requestObj.requestPayloadData, response, (1000 * 60 * 60), false);
          if (new ResponseValidator(response, true).status) {
            const {programEligibility}: any = response.responsePayloadData || null;
            const {autoFill} = programEligibility ? programEligibility : null;
            const elg = this.sdEligibility = (typeof autoFill === 'string' && autoFill.toUpperCase() === 'Y');
            this.asyncSDEligibility.next({
              autofillEnrolled: false,
              autofillEligible: elg
            });
          } else {
            this.asyncSDEligibility.next({});
            localConsole.log('Autofill Eligible service error', response);
          }
          return response;
        }));
  }

  /**
   * Create Drug List array
   */
  getDrugList(order: any, rxList: MdpDrug[]) {
    if (rxList && rxList.length) {
      return rxList.map(drug => {
        return {
          rxNumber: drug.rxNumber,
          storeNumber: drug.storeNumber
        };
      });
    }
    return order.prescriptionInfo.map(drug => {
      return {
        rxNumber: drug.rxNumber,
        storeNumber: drug.storeNumber
      };
    });
  }

}
